import { type ClipAssets } from '@air/api/types';

/**
 * Processed `assets.image` previews for Illustrator and SVG files are flattened
 * JPEGs. A CSS checkerboard only shows through pixels that still have alpha, so
 * these formats display the original and ask Imgix for an alpha-capable output.
 * `auto=format` prefers AVIF/WebP; `fm=png` is the fallback so the last hop is
 * not a JPEG that composites transparency away.
 */
const TRANSPARENT_PREVIEW_EXTENSIONS = new Set(['ai', 'svg']);

export const TRANSPARENT_PREVIEW_IMGIX_PARAMS = {
  auto: 'format',
  fm: 'png',
} as const;

export const TRANSPARENCY_CHECKERBOARD_CLASS = 'transparency-checkerboard';

export const transparencyCheckerboardClass = (preserveTransparency: boolean): string | undefined =>
  preserveTransparency ? TRANSPARENCY_CHECKERBOARD_CLASS : undefined;

export const transparentPreviewImgixParams = (preserveTransparency: boolean) =>
  preserveTransparency ? TRANSPARENT_PREVIEW_IMGIX_PARAMS : undefined;

type TransparentPreviewAssets = Partial<Pick<ClipAssets, 'image' | 'original'>>;

export type TransparentPreviewSource = {
  fallbackSrc: string | undefined;
  preserveTransparency: boolean;
  src: string | undefined;
};

export const isTransparentPreviewExtension = (ext: string | undefined): boolean => {
  const normalized = ext?.toLowerCase();

  return normalized !== undefined && TRANSPARENT_PREVIEW_EXTENSIONS.has(normalized);
};

/**
 * Share of a transparent preview that `object-cover` may crop before the card switches to contain.
 * Logos lose meaning when cropped, while posters barely change when a sliver is cut off.
 */
export const MAX_TRANSPARENT_COVER_CROP = 0.2;

export type ShouldContainTransparentPreviewParams = {
  cardWidth: number | undefined;
  cardHeight: number | undefined;
  imageWidth: number | undefined;
  imageHeight: number | undefined;
};

/** Contain when cover would crop more than `MAX_TRANSPARENT_COVER_CROP`, or when a size is unknown. */
export const shouldContainTransparentPreview = ({
  cardWidth,
  cardHeight,
  imageWidth,
  imageHeight,
}: ShouldContainTransparentPreviewParams): boolean => {
  if (!cardWidth || !cardHeight || !imageWidth || !imageHeight) {
    return true;
  }

  const cardAspect = cardWidth / cardHeight;
  const imageAspect = imageWidth / imageHeight;
  const coverCropFraction = 1 - Math.min(cardAspect / imageAspect, imageAspect / cardAspect);

  return coverCropFraction > MAX_TRANSPARENT_COVER_CROP;
};

/** Imgix returns SVG originals unrasterized, so these previews are normalized and inlined instead. */
export const isSvgExtension = (ext: string | undefined): boolean => ext?.toLowerCase() === 'svg';

/** Empty and null asset URLs are missing renditions, not addresses to request. */
const toOptionalAssetUrl = (value: string | null | undefined): string | undefined => value || undefined;

export const getTransparentPreviewSource = (
  ext: string,
  assets: TransparentPreviewAssets,
): TransparentPreviewSource => {
  const image = toOptionalAssetUrl(assets.image);
  const original = toOptionalAssetUrl(assets.original);

  if (isTransparentPreviewExtension(ext) && original) {
    return {
      fallbackSrc: image,
      preserveTransparency: true,
      src: original,
    };
  }

  return {
    fallbackSrc: image,
    preserveTransparency: false,
    src: image,
  };
};

type TransparentPreviewAttempt = {
  failedSrc: string | undefined;
  processedPreviewSrc: string | undefined;
  src: string | undefined;
};

/** The original rendition failed and the processed JPEG is a different file we have not tried yet. */
export const canRetryTransparentPreview = ({
  failedSrc,
  processedPreviewSrc,
  src,
}: TransparentPreviewAttempt): boolean =>
  Boolean(src && processedPreviewSrc && processedPreviewSrc !== src && failedSrc !== src);

/** After the original fails, display the processed JPEG. A new `src` drops the previous failure. */
export const transparentPreviewDisplaySrc = ({
  failedSrc,
  processedPreviewSrc,
  src,
}: TransparentPreviewAttempt): string | undefined => {
  if (src && processedPreviewSrc && failedSrc === src && processedPreviewSrc !== src) {
    return processedPreviewSrc;
  }

  return src;
};

/** Use the original when the processed preview would flatten alpha. */
export const preferredPreviewSrc = (preview: TransparentPreviewSource, processedSrc: string): string => {
  if (preview.preserveTransparency && preview.src) {
    return preview.src;
  }

  return processedSrc;
};
